import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Refresh,
  Download,
  Clear,
  FilterList,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { botApi } from '../services/api';
import { socketService } from '../services/socket';

interface LogViewerProps {
  open: boolean;
  onClose: () => void;
  botName: string;
}

const LogViewer: React.FC<LogViewerProps> = ({ open, onClose, botName }) => {
  const [logs, setLogs] = useState<string>('');
  const [filteredLogs, setFilteredLogs] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [logLevel, setLogLevel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [maxLines, setMaxLines] = useState(100);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Fetch initial logs
  const { data: initialLogs, refetch, isLoading } = useQuery({
    queryKey: ['botLogs', botName, maxLines],
    queryFn: () => botApi.getBotLogs(botName, maxLines),
    enabled: open && !!botName,
    refetchInterval: autoRefresh ? refreshInterval * 1000 : false,
  });

  useEffect(() => {
    if (initialLogs) {
      setLogs(initialLogs);
    }
  }, [initialLogs]);

  // Socket.IO real-time logs
  useEffect(() => {
    if (!open || !botName) return;

    const handleLogsUpdate = (data: { botName: string; logs: string }) => {
      if (data.botName === botName) {
        setLogs(data.logs);
      }
    };

    if (autoRefresh) {
      socketService.subscribeToLogs(botName, handleLogsUpdate, maxLines);
    }

    return () => {
      socketService.unsubscribeFromLogs();
    };
  }, [open, botName, autoRefresh, maxLines]);

  // Filter logs
  useEffect(() => {
    let filtered = logs;

    // Filter by log level
    if (logLevel !== 'all') {
      const lines = logs.split('\n');
      const levelKeywords = {
        error: ['ERROR', 'error', 'Error'],
        warning: ['WARN', 'WARNING', 'warn', 'warning'],
        info: ['INFO', 'info'],
        debug: ['DEBUG', 'debug']
      };
      
      const keywords = levelKeywords[logLevel as keyof typeof levelKeywords] || [];
      filtered = lines
        .filter(line => keywords.some(keyword => line.includes(keyword)))
        .join('\n');
    }

    // Filter by search term
    if (searchTerm) {
      const lines = filtered.split('\n');
      filtered = lines
        .filter(line => 
          line.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .join('\n');
    }

    setFilteredLogs(filtered);
  }, [logs, logLevel, searchTerm]);

  // Auto scroll to bottom
  useEffect(() => {
    if (logContainerRef.current && autoRefresh) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [filteredLogs, autoRefresh]);

  const handleRefresh = () => {
    refetch();
    toast.success('Loglar yenilendi');
  };

  const handleDownload = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${botName}_logs_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Loglar indirildi');
  };

  const handleClear = () => {
    setLogs('');
    setFilteredLogs('');
    toast('Log görünümü temizlendi');
  };

  const getLogLineColor = (line: string) => {
    if (line.includes('ERROR') || line.includes('error')) return '#f44336';
    if (line.includes('WARN') || line.includes('warning')) return '#ff9800';
    if (line.includes('INFO') || line.includes('info')) return '#2196f3';
    if (line.includes('DEBUG') || line.includes('debug')) return '#9e9e9e';
    return '#000000';
  };

  const renderLogLine = (line: string, index: number) => {
    const color = getLogLineColor(line);
    const isHighlighted = searchTerm && 
      line.toLowerCase().includes(searchTerm.toLowerCase());

    return (
      <div
        key={index}
        style={{
          color,
          backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '12px',
          lineHeight: '1.4',
          padding: '2px 4px',
          borderLeft: isHighlighted ? '3px solid #ffc107' : 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {line}
      </div>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          height: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '20px 20px 0 0',
        p: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            📊 {botName.toUpperCase()} - Log Görüntüleyici
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              label={autoRefresh ? '🟢 Canlı' : '🔴 Durduruldu'} 
              color={autoRefresh ? 'success' : 'default'}
              sx={{ 
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            />
            <Chip 
              label={`📄 ${filteredLogs.split('\n').filter(line => line.trim()).length} satır`}
              sx={{ 
                background: 'rgba(255,255,255,0.9)',
                color: '#667eea',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              }
              label="Otomatik Yenile"
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Yenileme (sn)</InputLabel>
              <Select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
              >
                <MenuItem value={1}>1 saniye</MenuItem>
                <MenuItem value={5}>5 saniye</MenuItem>
                <MenuItem value={10}>10 saniye</MenuItem>
                <MenuItem value={30}>30 saniye</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Log Seviyesi</InputLabel>
              <Select
                value={logLevel}
                onChange={(e) => setLogLevel(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="error">Hata</MenuItem>
                <MenuItem value="warning">Uyarı</MenuItem>
                <MenuItem value="info">Bilgi</MenuItem>
                <MenuItem value="debug">Debug</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Maksimum Satır</InputLabel>
              <Select
                value={maxLines}
                onChange={(e) => setMaxLines(Number(e.target.value))}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <FilterList sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <Box sx={{ flexGrow: 1 }} />

            <IconButton
              onClick={handleRefresh}
              disabled={isLoading}
              title="Yenile"
            >
              <Refresh />
            </IconButton>

            <IconButton
              onClick={handleDownload}
              title="İndir"
            >
              <Download />
            </IconButton>

            <IconButton
              onClick={handleClear}
              title="Temizle"
            >
              <Clear />
            </IconButton>
          </Box>
        </Paper>

        {/* Log Content */}
        <Paper 
          sx={{ 
            height: '500px', 
            overflow: 'auto',
            backgroundColor: '#1e1e1e',
            p: 2
          }}
          ref={logContainerRef}
        >
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="white">Loglar yükleniyor...</Typography>
            </Box>
          ) : filteredLogs ? (
            <Box>
              {filteredLogs.split('\n').map((line, index) => 
                line.trim() ? renderLogLine(line, index) : null
              )}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="white">Log bulunamadı</Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogViewer;
